import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  BarElement, // Required for gradients/area charts
} from "chart.js";
import { Bar, Line } from "react-chartjs-2";
import { faker } from "@faker-js/faker";
import { createAndPublishServiceChart } from "../../../api/network";
import { useEffect, useState } from "react";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
);

export const options = {
  responsive: true,
  maintainAspectRatio: false, // Allows the container to control height
  interaction: {
    mode: "index",
    intersect: false, // Better UX: hover anywhere on the vertical line
  },
  plugins: {
    legend: {
      position: "top",
      align: "end", // Align legend to the right
      labels: {
        usePointStyle: true, // Circular legend icons look cleaner
        pointStyle: "circle",
        padding: 15,
        font: { size: 12, family: "'Poppins', sans-serif" },
      },
    },
    tooltip: {
      backgroundColor: "rgba(255, 255, 255, 0.9)",
      titleColor: "#1e293b",
      bodyColor: "#1e293b",
      borderColor: "#e2e8f0",
      borderWidth: 1,
      padding: 12,
      boxPadding: 6,
      usePointStyle: true,
      titleFont: {
        family: "'Poppins', sans-serif",
        size: 12,
        weight: "600",
      },
      bodyFont: {
        family: "'Poppins', sans-serif",
        size: 12,
      },
    },
  },
  scales: {
    x: {
      grid: { display: false }, // Hide vertical grid lines for a cleaner look
      ticks: {
        color: "#94a3b8",
        maxRotation: 0,
        font: { size: 12, family: "'Poppins', sans-serif" },
      },
    },
    y: {
      beginAtZero: true,
      border: { dash: [4, 4] }, // Dashed horizontal lines
      grid: { color: "#f1f5f9" },
      ticks: {
        color: "#94a3b8",
        font: { size: 12, family: "'Poppins', sans-serif" },
      },
    },
  },
  elements: {
    line: {
      tension: 0.4, // Adds smooth Bezier curves
      borderWidth: 2,
      capBezierPoints: true,
    },
    point: {
      radius: 0, // Hide points by default
      hoverRadius: 4, // Show on hover for "pop" effect
    },
  },
};

const labels = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul"];

export const data = {
  labels,
  datasets: [
    {
      label: "Issued",
      data: labels.map(() => faker.number.int({ min: 0, max: 1000 })),
      borderColor: "rgb(99, 102, 241)", // Modern Indigo
      backgroundColor: (context) => {
        const ctx = context.chart.ctx;
        const gradient = ctx.createLinearGradient(0, 0, 0, 400);
        gradient.addColorStop(0, "rgba(99, 102, 241, 0.2)");
        gradient.addColorStop(1, "rgba(99, 102, 241, 0)");
        return gradient;
      },
      fill: true,
    },
    {
      label: "Verified",
      data: labels.map(() => faker.number.int({ min: 0, max: 1000 })),
      borderColor: "rgb(20, 184, 166)", // Modern Teal
      backgroundColor: (context) => {
        const ctx = context.chart.ctx;
        const gradient = ctx.createLinearGradient(0, 0, 0, 400);
        gradient.addColorStop(0, "rgba(20, 184, 166, 0.2)");
        gradient.addColorStop(1, "rgba(20, 184, 166, 0)");
        return gradient;
      },
      fill: true,
    },
  ],
};

export default function CreateAndPublishServiceChart() {
  const [groupBy, setGroupBy] = useState("week");
  const [chartData, setChartData] = useState({ created: [], published: [] });
  const [dateRange, setDateRange] = useState({
    start: "2024-12-01",
    end: "2026-04-27",
  });
  const fetchChart = async () => {
    const networkId = localStorage.getItem("networkId");
    if (!networkId) return;
    console.log("netid", networkId);

    try {
      const data = await createAndPublishServiceChart(
        dateRange.start,
        dateRange.end,
        groupBy,
        networkId,
      );
      // Ensure we set the array (check if your API returns .result or .data)
      setChartData(data || { created: [], published: [] });
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchChart();
  }, [dateRange, groupBy]);

  const allDates = [
    ...new Set([
      ...(chartData?.created || []).map((i) => i.date),
      ...(chartData?.published || []).map((i) => i.date),
    ]),
  ].sort();

  const mapData = (arr) => {
    const map = {};
    (arr || []).forEach((i) => {
      map[i.date] = Number(i.count);
    });
    return map;
  };

  const createdMap = mapData(chartData?.created);
  const publishedMap = mapData(chartData?.published);
  const chartConfig = {
    labels: allDates.map((date) => {
      const d = new Date(date.split("T")[0]);

      const shouldShowYear =
        new Date(dateRange.start).getFullYear() !==
        new Date(dateRange.end).getFullYear();

      return d.toLocaleDateString("en-IN", {
        ...(groupBy === "month" && { month: "short" }),
        ...(groupBy === "day" && { day: "2-digit", month: "short" }),
        ...(groupBy === "week" && { day: "2-digit", month: "short" }),
        ...(shouldShowYear && { year: "numeric" }),
      });
    }),

    datasets: [
      {
        label: "Created",
        data: allDates.map((date) => createdMap[date] || 0),
        borderColor: "#6366f1",
        // backgroundColor: "rgba(99,102,241,0.15)",
        backgroundColor: "#7f64d6",
        fill: true,
      },
      {
        label: "Published",
        data: allDates.map((date) => publishedMap[date] || 0),
        borderColor: "#10b981",
        // backgroundColor: "rgba(16,185,129,0.15)",
        backgroundColor: "#15d79e",
        fill: true,
      },
    ],
  };
  return (
    <div
      style={{
        height: "400px",
        width: "100%",
        background: "#fff",
        borderRadius: "12px",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {" "}
      <div className="chart-controls d-flex gap-3 align-items-end">
        <div className="date-filter">
          <div className="date-field">
            <label>From</label>
            <input
              type="date"
              value={dateRange.start}
              onChange={(e) =>
                setDateRange((prev) => ({
                  ...prev,
                  start: e.target.value,
                }))
              }
            />
          </div>

          <div className="date-field">
            <label>To</label>
            <input
              type="date"
              value={dateRange.end}
              onChange={(e) =>
                setDateRange((prev) => ({
                  ...prev,
                  end: e.target.value,
                }))
              }
            />
          </div>
        </div>

        <div className="btn-group ">
          <button
            type="button"
            className="btn btn-sm btn-primary dropdown-toggle rounded-3"
            data-bs-toggle="dropdown"
            aria-expanded="false"
          >
            {groupBy === "day"
              ? "Daily"
              : groupBy === "week"
                ? "Weekly"
                : "Monthly"}
          </button>

          <ul className="dropdown-menu">
            <li>
              <button
                className={`dropdown-item ${groupBy === "day" ? "active" : ""}`}
                onClick={() => setGroupBy("day")}
              >
                Daily
              </button>
            </li>
            <li>
              <button
                className={`dropdown-item ${groupBy === "week" ? "active" : ""}`}
                onClick={() => setGroupBy("week")}
              >
                Weekly
              </button>
            </li>
            <li>
              <button
                className={`dropdown-item ${groupBy === "month" ? "active" : ""}`}
                onClick={() => setGroupBy("month")}
              >
                Monthly
              </button>
            </li>
          </ul>
        </div>
      </div>
      <div style={{ flex: 1, position: "relative", minHeight: 0 }}>
        <Bar options={options} data={chartConfig} />
      </div>
    </div>
  );
}
